import { SKELETON_FILE_DIRECTIVE } from '@agilelab/plugin-wb-catalog-extension-common';
import {
  customAlertApiRef,
  MonacoEditor,
  WbBreadcrumb,
  WbBreadcrumbs,
  WbCard,
  WbCardContent,
  WbEditor,
  WbHeader,
} from '@agilelab/plugin-wb-platform';
import { Content } from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { Button, Grid, Typography } from '@material-ui/core';
import { useCallback, useMemo, useRef, useState } from 'react';
import yaml from 'yaml';
import { panelCatalogApiRef } from '../../../api';
import { WbPage } from '@agilelab/plugin-wb-platform';

export function SkeletonRenderingPreviewPage() {
  const [catalogInfo, setCatalogInfo] = useState(
    `${SKELETON_FILE_DIRECTIVE}\nkind: Component\r\nspec:\r\n  mesh:\r\n    name: \${{ parameters.name }}`,
  );
  const [parameters, setParameters] = useState(
    'parameters:\n  name: Test Component',
  );
  const [renderedCatalogInfo, setRenderedCatalogInfo] = useState('');
  const [_, setMonacoEditor] = useState<MonacoEditor>();
  const panelCatalogApi = useApi(panelCatalogApiRef);
  const alertApi = useApi(customAlertApiRef);
  const [loading, setLoading] = useState(false);
  const renderedOutputRef = useRef<HTMLDivElement | null>(null);

  const handleRender = useCallback(async () => {
    try {
      setLoading(true);
      const params = parameters ? yaml.parse(parameters) : {};
      const renderingRes = await panelCatalogApi.renderSkeletonEntity({
        catalogInfo: catalogInfo.startsWith(SKELETON_FILE_DIRECTIVE)
          ? catalogInfo
          : `${SKELETON_FILE_DIRECTIVE}\n${catalogInfo}`,
        parameters: params,
      });
      setRenderedCatalogInfo(yaml.stringify(renderingRes.catalogInfo));
      renderedOutputRef.current?.scrollIntoView({ behavior: 'smooth' });
    } catch (error) {
      alertApi.post({ error, severity: 'error' });
      setRenderedCatalogInfo('');
    }
    setLoading(false);
  }, [alertApi, catalogInfo, parameters, panelCatalogApi]);

  const editorOptions = useMemo(
    () => ({
      selectOnLineNumbers: true,
      contextmenu: false,
      readOnly: loading,
      diffCodeLens: false,
      originalEditable: false,
      glyphMargin: true,
      linkedEditing: true,
      renderMarginRevertIcon: true,
      minimap: {
        enabled: true,
      },
    }),
    [loading],
  );

  return (
    <WbPage themeId="home">
      <WbHeader
        title="Skeleton Rendering Preview"
        breadcrumbs={
          <WbBreadcrumbs aria-label="breadcrumb">
            <WbBreadcrumb>Administration</WbBreadcrumb>
            <WbBreadcrumb>Builder Tools</WbBreadcrumb>
            <WbBreadcrumb>Skeleton Rendering Preview</WbBreadcrumb>
          </WbBreadcrumbs>
        }
      />
      <Content>
        <WbCard title="Renderer">
          <WbCardContent>
            <Grid container spacing={2} direction="column">
              <Grid container item spacing={2}>
                <Grid item xs={6}>
                  <Typography
                    variant="body1"
                    style={{ fontWeight: 'bold', padding: '10px' }}
                  >
                    Skeleton catalog info
                  </Typography>
                  <WbEditor
                    theme="white"
                    height="400px"
                    language="plaintext"
                    value={catalogInfo}
                    onChange={value => setCatalogInfo(value || '')}
                    options={editorOptions}
                    onMount={editor => {
                      setMonacoEditor(editor);
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography
                    variant="body1"
                    style={{ fontWeight: 'bold', padding: '10px' }}
                  >
                    Parameters
                  </Typography>
                  <WbEditor
                    theme="white"
                    height="400px"
                    language="yaml"
                    value={parameters}
                    onChange={value => setParameters(value || '')}
                    options={editorOptions}
                    onMount={editor => {
                      setMonacoEditor(editor);
                    }}
                  />
                </Grid>
              </Grid>
              <Grid
                item
                container
                justifyContent="center"
                style={{ padding: '10px' }}
              >
                <Button
                  color="primary"
                  size="large"
                  variant="contained"
                  disabled={loading}
                  onClick={handleRender}
                >
                  Render
                </Button>
              </Grid>
              <Grid item ref={renderedOutputRef}>
                <Typography
                  variant="body1"
                  style={{ fontWeight: 'bold', padding: '10px' }}
                >
                  Rendered catalog info
                </Typography>
                <WbEditor
                  theme="white"
                  height="400px"
                  language="yaml"
                  value={loading ? 'Rendering...' : renderedCatalogInfo}
                  options={{ ...editorOptions, readOnly: false }}
                  onMount={editor => {
                    setMonacoEditor(editor);
                  }}
                />
              </Grid>
            </Grid>
          </WbCardContent>
        </WbCard>
      </Content>
    </WbPage>
  );
}
